import { useCallback } from "react"
import { useAuthBearerFetch, bearerFetcher, MatsuriError } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import { uploadBlobWithPresignedPut } from "@/utils/presignedUpload"

// 担当清掃がゴミストッカーチェック対象かどうか。
interface GarbageStorageCheckTargetResponse {
    isTarget: boolean
    areaName?: string | null
}

// ゴミストッカーの見本写真。物件上書きが無ければ global へフォールバック。画像が無ければ url は null。
interface GarbageStorageSampleImageResponse {
    url?: string | null
    updatedAt?: number | null
}

// ゴミストッカーの情報。
// 民泊用ゴミ捨て場の南京錠（RoomGarbageInfo）とは別物。未設定環境では各フィールドが null。
export interface GarbageStorageKeyInfoResponse {
    // 設置場所（mw_location）
    location?: string | null
    // 解錠方法（mw_how_to_unlock）
    howToUnlock?: string | null
    // 解錠番号（mw_unlock_number）
    unlockNumber?: string | null
}

// 既に報告済みのゴミストッカー確認（未報告なら API は null を返す）。
export interface GarbageStorageCheckImage {
    id: string
    url: string
    createdAt: number
}
export interface GarbageStorageCheckResponse {
    cleaningId: string
    isOverflowing: boolean
    reportedBy: string
    // 報告日時（UNIX 秒）
    reportedAt: number
    images: GarbageStorageCheckImage[]
}

/**
 * ゴミストッカー確認画面のデータ取得（対象判定・見本写真・鍵情報）と確認報告（写真アップロード→complete）をまとめたフック。
 *
 * - `isTarget`: ゴミストッカーチェック対象か（取得前は undefined）。対象でない清掃はこの画面をスキップする。
 * - `areaName`: 対象エリア名（無ければ undefined）
 * - `sampleImageUrl`: 見本写真 URL（無ければ undefined）
 * - `keyInfo`: ゴミストッカーの鍵情報（全フィールド空なら undefined）
 * - `isLoading`: 対象判定・対象時の見本写真/鍵情報の取得が進行中か
 * - `error`: 対象判定（check-target）の取得に失敗したエラー。
 *   見本写真・鍵情報・既存報告の失敗は degrade させ、ここには含めない（報告は継続できる）。
 * - `uploadImage(blob, signal)`: 1枚分の issue + S3 PUT を行い uploadId を返す（useImmediatePhotoUpload に注入）
 * - `completeCheck({ isOverflowing, imageUploadIds })`: ゴミストッカー確認を報告する
 */
export const useGarbageCheck = (cleaningId: string) => {
    const { token } = useAuth()

    // まず対象判定。対象でなければ見本写真・鍵情報の取得は行わない。
    const {
        data: checkTarget,
        isLoading: isCheckTargetLoading,
        error: checkTargetError
    } = useAuthBearerFetch<GarbageStorageCheckTargetResponse>(
        token,
        cleaningId
            ? m2mCleaning_cleanerV4.getGarbageStorageCheckTarget({ cleaningId })
            : null,
        {
            method: m2mCleaning_cleanerV4.getGarbageStorageCheckTarget.method
        }
    )

    const isTarget = checkTarget?.isTarget

    // 見本写真・鍵情報・既存報告の取得失敗は報告をブロックしない（各表示が degrade するだけ）。
    const { data: sampleImage, isLoading: isSampleImageLoading } =
        useAuthBearerFetch<GarbageStorageSampleImageResponse>(
            token,
            cleaningId && isTarget
                ? m2mCleaning_cleanerV4.getGarbageStorageSampleImageForCleaner({
                      cleaningId
                  })
                : null,
            {
                method: m2mCleaning_cleanerV4
                    .getGarbageStorageSampleImageForCleaner.method
            }
        )

    const { data: keyInfo, isLoading: isKeyInfoLoading } =
        useAuthBearerFetch<GarbageStorageKeyInfoResponse>(
            token,
            cleaningId && isTarget
                ? m2mCleaning_cleanerV4.getGarbageStorageKeyInfoForCleaner({
                      cleaningId
                  })
                : null,
            {
                method: m2mCleaning_cleanerV4.getGarbageStorageKeyInfoForCleaner
                    .method
            }
        )

    // 既存の報告（未報告なら null）。報告済みなら報告日時・写真を表示に使う。
    // 取得失敗時は undefined（=未報告扱い）になり、通常の報告フローを継続できる。
    const { data: existingReport, isLoading: isExistingReportLoading } =
        useAuthBearerFetch<GarbageStorageCheckResponse | null>(
            token,
            cleaningId && isTarget
                ? m2mCleaning_cleanerV4.getGarbageStorageCheck({ cleaningId })
                : null,
            {
                method: m2mCleaning_cleanerV4.getGarbageStorageCheck.method
            }
        )

    const uploadImage = useCallback(
        async (blob: Blob, signal: AbortSignal) => {
            return uploadBlobWithPresignedPut({
                token,
                issueUrl:
                    m2mCleaning_cleanerV4.issueGarbageStorageCheckImageUploadUrl(
                        {
                            cleaningId
                        }
                    ),
                blob,
                signal
            })
        },
        [token, cleaningId]
    )

    const completeCheck = useCallback(
        async ({
            isOverflowing,
            imageUploadIds
        }: {
            isOverflowing: boolean
            imageUploadIds: string[]
        }) => {
            const result = await bearerFetcher(
                token ?? "",
                m2mCleaning_cleanerV4.completeGarbageStorageCheck({
                    cleaningId
                }),
                {
                    method: "POST",
                    body: JSON.stringify({ isOverflowing, imageUploadIds })
                }
            )
            if (result.error) {
                throw result.error
            }
        },
        [token, cleaningId]
    )

    // 対象判定が解決するまで（と対象時は見本写真/鍵情報/既存報告の取得中も）は読込中扱い。
    const isLoading =
        isCheckTargetLoading ||
        (isTarget === true &&
            (isSampleImageLoading ||
                isKeyInfoLoading ||
                isExistingReportLoading))

    // ブロックするのは対象判定の失敗のみ（対象か分からないまま進めると誤報告になるため）。
    // 見本写真・鍵情報・既存報告の失敗は degrade させ、報告は継続できるようにする。
    const error: MatsuriError | undefined = checkTargetError

    return {
        isTarget,
        areaName: checkTarget?.areaName ?? undefined,
        sampleImageUrl: sampleImage?.url ?? undefined,
        keyInfo:
            keyInfo &&
            (keyInfo.location || keyInfo.howToUnlock || keyInfo.unlockNumber)
                ? keyInfo
                : undefined,
        // 報告済みなら報告内容（日時・溢れ有無・写真）。未報告なら undefined。
        existingReport: existingReport ?? undefined,
        isLoading,
        error,
        uploadImage,
        completeCheck
    }
}
